import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  accountQuery,
  forgotPassword,
  forgotPasswordSms,
  forgotUsername,
} from '@/api';
import {
  ForgotPasswordModal,
  type ForgotPasswordStep,
  type ForgotPasswordSuccessKind,
} from '@/ui/auth';

export const Route = createFileRoute('/forgotpassword')({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: account } = useQuery(accountQuery());
  const [step, setStep] = useState<ForgotPasswordStep>('chooser');
  const [successKind, setSuccessKind] = useState<ForgotPasswordSuccessKind>('email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chatEnabled = account?.Chat === true;

  const leave = () => {
    void navigate({ href: '/login' });
  };

  const handleSubmitEmail = async (email: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await forgotPassword({ Email: email });
      if (result.Success === false) {
        setError(
          t(result.Error || 'EmailAddressNotFound', {
            defaultValue: 'Email address not found',
          }),
        );
        return;
      }
      setSuccessKind('email');
      setStep('success');
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : 'EmailAddressNotFound';
      setError(t(message, { defaultValue: message }));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitSms = async (telephone: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await forgotPasswordSms({ Telephone: telephone });
      if (result.Success === false || !result.Guid) {
        setError(
          t(result.Error || 'PhoneNumberNotFound', {
            defaultValue: 'Phone number not found',
          }),
        );
        return;
      }
      await navigate({
        to: '/resetpassword/$guid/$channel',
        params: { guid: result.Guid, channel: 'sms' },
      });
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : 'PhoneNumberNotFound';
      setError(t(message, { defaultValue: message }));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitUsername = async (email: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await forgotUsername({ Email: email });
      if (result.Success === false) {
        setError(
          t(result.Error || 'EmailAddressNotFound', {
            defaultValue: 'Email address not found',
          }),
        );
        return;
      }
      setSuccessKind('username');
      setStep('success');
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : 'EmailAddressNotFound';
      setError(t(message, { defaultValue: message }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ForgotPasswordModal
      open
      dismissible
      onClose={leave}
      loading={loading}
      error={error}
      account={account}
      chatEnabled={chatEnabled}
      step={step}
      successKind={successKind}
      onStepChange={(next) => {
        setError(null);
        setStep(next);
      }}
      onSubmitEmail={handleSubmitEmail}
      onSubmitSms={handleSubmitSms}
      onSubmitUsername={handleSubmitUsername}
    />
  );
}
